/**
 * INSTALLATION INSTRUCTIONS:
 *
 * 1. Add this file to your theme's assets folder
 * 2. Add to theme.liquid before closing </body> tag:
 *     <script type="module" src="{{ 'locator.js' | asset_url }}"></script>
 * 3. You may control:
 *    - the location detection frequency by changing the INVALIDATE_LOCATION_AFTER_HOURS constant.
 *    - the storage key by changing the LOCATION_STORAGE_KEY constant.
 * 4. An event "userLocationDetected" is dispatched when the location is detected.
 */

(() => {
    const LOCATION_STORAGE_KEY = "userCountryCode";
    const LOCATION_STORAGE_TIMESTAMP_KEY = "userCountryCodeTimestamp";
    const INVALIDATE_LOCATION_AFTER_HOURS = 24;

    class LocationDetector {
        constructor() {
            this.init();
        }

        async init() {
            if (!this.isStorageRestricted()) {
                const storedInfo = this.getStoredLocation();
                const storedLocation = storedInfo?.location;
                const storedTimestamp = storedInfo?.timestamp;

                if (storedLocation && this.isLocationValid(storedTimestamp)) {
                    this.dispatchLocationEvent(storedLocation);
                    return;
                }
            }

            // If storage is restricted or stored location is invalid/missing, detect new location
            const locationData = await this.detectAndStoreLocation();

            if (locationData) {
                return;
            }

            // Fallback to default if everything fails
            this.dispatchLocationEvent("DEFAULT");
        }

        isStorageRestricted() {
            try {
                return typeof localStorage === "undefined";

            } catch (e) {
                return true;
            }
        }

        isLocationValid(storedTimestamp) {
            if (!storedTimestamp) return true;

            // Check if stored location is less than (INVALIDATE_LOCATION_AFTER_HOURS) hours old
            const invalidateLocationAfterHours =
                INVALIDATE_LOCATION_AFTER_HOURS * 60 * 60 * 1000;
            const timestamp = new Date(storedTimestamp).getTime();
            return Date.now() - timestamp < invalidateLocationAfterHours;
        }

        getStoredLocation() {
            if (this.isStorageRestricted()) {
                return null;
            }

            try {
                const storedLocation = localStorage.getItem(LOCATION_STORAGE_KEY);
                const storedTimestamp = localStorage.getItem(
                    LOCATION_STORAGE_TIMESTAMP_KEY
                );

                return {
                    location: storedLocation ? JSON.parse(storedLocation) : null,
                    timestamp: storedTimestamp ? JSON.parse(storedTimestamp) : null,
                };
            } catch (error) {
                return null;
            }
        }

        async detectAndStoreLocation() {
            try {
                const response = await fetch("/browsing_context_suggestions.json");
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (!data?.detected_values?.country) {
                    throw new Error("No country data in API response");
                }

                const locationData = {
                    country_code: data.detected_values.country.handle,
                    country_name: data.detected_values.country.name,
                    timestamp: new Date().toISOString(),
                };

                if (!this.isStorageRestricted()) {
                    try {
                        localStorage.setItem(
                            LOCATION_STORAGE_KEY,
                            JSON.stringify(locationData.country_code)
                        );
                        localStorage.setItem(
                            LOCATION_STORAGE_TIMESTAMP_KEY,
                            JSON.stringify(locationData.timestamp)
                        );
                    } catch (storageError) {
                    }
                }

                this.dispatchLocationEvent(locationData.country_code);
                return locationData;
            } catch (error) {
                this.dispatchLocationEvent("DEFAULT");
                return null;
            }
        }

        dispatchLocationEvent(location) {
            window.dispatchEvent(
                new CustomEvent("userLocationDetected", {
                    detail: location,
                })
            );
        }
    }

    // Initialize on DOM load
    document.addEventListener("DOMContentLoaded", () => {
        new LocationDetector();
    });
})();
